<template>
  <!-- Spacer: pushes page content below the fixed bar -->
  <div class="h-14 w-full" />

  <header class="fixed top-0 left-0 right-0 h-14 z-50 app-topbar flex items-center gap-3 px-3 sm:px-5 md:pl-64">
    <!-- Logo -->
    <router-link to="/" class="app-link shrink-0 mr-1" :aria-label="$t('ui.eqsysHome')">
      <img src="/eqsys-white.svg" alt="eqSys" style="height:28px; width:80px; object-fit:contain;" />
    </router-link>

    <!-- Search — grows to fill available space -->
    <div class="flex-1 min-w-0">
      <LiveSearch />
    </div>

    <button
      v-if="authStore.isAuthenticated"
      type="button"
      class="btn ghost small shrink-0 hidden sm:inline-flex"
      :title="$t('ux.shellToggle')"
      @click="shell.toggle()"
    >
      {{ shell.isWarehouse ? $t('ux.shellWarehouse') : $t('ux.shellOffice') }}
    </button>

    <!-- Notification bell -->
    <div class="relative shrink-0" v-if="authStore.isAuthenticated">
      <button
        class="relative btn-icon"
        @click="notifOpen = !notifOpen"
        :title="$t('nav.notifications')"
        :aria-expanded="notifOpen"
      >
        <MdiBellOutline />
        <span v-if="unreadCount" class="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-red-500 pointer-events-none" />
      </button>

      <Transition name="dropdown">
        <div
          v-if="notifOpen"
          class="absolute right-0 mt-2 w-80 rounded-xl shadow-2xl z-50 overflow-hidden"
          style="background:#1E1E1E; border:1px solid rgba(255,255,255,0.10);"
        >
          <div class="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span class="text-sm font-semibold text-white">{{ $t('nav.notifications') }}</span>
            <div class="flex items-center gap-3">
              <button v-if="notifications.some(n => n.is_read)" @click="clearRead" class="text-xs text-gray-400 hover:text-gray-200">{{ $t('nav.clearRead') }}</button>
              <button v-if="unreadCount" @click="markAllRead" class="text-xs text-green-400 hover:text-green-300">{{ $t('nav.markAllRead') }}</button>
            </div>
          </div>

          <div v-if="notifLoading" class="p-4 text-xs text-gray-400 text-center">{{ $t('common.loading') }}</div>
          <div v-else-if="!notifications.length" class="p-4 text-xs text-gray-500 text-center">{{ $t('nav.noNotifications') }}</div>
          <div v-else class="max-h-80 overflow-y-auto divide-y divide-white/5">
            <div
              v-for="n in notifications"
              :key="n.id"
              class="flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors group"
              :class="[n.is_read ? 'opacity-60' : 'bg-white/3', 'hover:bg-white/5']"
              @click="openNotification(n)"
            >
              <!-- Restock alert icon -->
              <div v-if="n.notification_type === 'restock_alert'"
                   class="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-orange-900/60">
                <MdiAlertDecagram class="w-4 h-4 text-orange-400" />
              </div>
              <div v-else class="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-white/10">
                <MdiBellOutline class="w-4 h-4 text-gray-400" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm text-white font-medium leading-tight truncate">{{ n.subject ?? n.notification_type }}</div>
                <div v-if="n.message" class="text-xs text-gray-400 mt-0.5 line-clamp-2">{{ n.message }}</div>
              </div>
              <div class="flex items-center gap-1.5 shrink-0 mt-0.5">
                <div v-if="!n.is_read" class="w-2 h-2 rounded-full bg-blue-400" />
                <button
                  class="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-white transition-opacity p-0.5 -mr-1"
                  :title="$t('common.dismiss')"
                  @click="dismiss(n, $event)"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
      <div v-if="notifOpen" class="fixed inset-0 z-40" @click="notifOpen = false" />
    </div>

    <!-- Tenant switcher -->
    <div class="relative shrink-0" v-if="tenants && tenants.length">
      <button
        class="btn ghost text-sm flex items-center gap-1.5 py-1.5 px-3"
        @click="tenantOpen = !tenantOpen"
        :aria-expanded="tenantOpen"
        :aria-label="$t('nav.switchTenant')"
      >
        <span class="truncate max-w-[9rem] hidden sm:inline">{{ currentTenantName || $t('nav.tenant') }}</span>
        <!-- Avatar initial on small screens -->
        <span class="sm:hidden w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold uppercase select-none" style="background:color-mix(in srgb,#1A3326 70%,transparent);color:#A8D0C1">
          {{ (currentTenantName || 'T').charAt(0) }}
        </span>
        <svg class="shrink-0" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6"/></svg>
      </button>

      <Transition name="dropdown">
        <div
          v-if="tenantOpen"
          class="absolute right-0 mt-2 w-60 rounded-xl shadow-2xl z-50 overflow-hidden"
          style="background:#1E1E1E; border:1px solid rgba(255,255,255,0.10);"
        >
          <div
            v-for="t in tenants"
            :key="t.slug"
            class="p-3 flex items-center justify-between hover:bg-white/5 active:bg-white/10 transition-colors border-b border-white/5 last:border-0 cursor-pointer"
            @click="selectTenant(t)"
          >
            <div>
              <div class="text-sm font-medium text-white">{{ t.name }}</div>
              <div class="text-xs text-gray-400 mt-0.5">{{ t.slug }} · {{ t.role }}</div>
            </div>
            <div class="flex items-center gap-2 ml-2 shrink-0">
              <span v-if="t.slug === currentTenantSlug" class="text-xs font-medium" style="color:#A8D0C1">{{ $t('common.active') }}</span>
              <span
                v-else-if="t.has_session === false"
                class="inline-flex items-center"
              >
                <MdiLockOutline
                  class="w-4 h-4 text-yellow-400"
                  aria-hidden="true"
                  focusable="false"
                />
                <span class="sr-only">{{ $t('nav.requiresStepUp') }}</span>
              </span>
            </div>
          </div>
          <div v-if="tenantError" class="p-3 text-xs text-red-400">{{ tenantError }}</div>
        </div>
      </Transition>
      <div v-if="tenantOpen" class="fixed inset-0 z-40" @click="tenantOpen = false" />
    </div>
  </header>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import LiveSearch from '../search/LiveSearch.vue';
import MdiLockOutline from '~icons/mdi/lock-outline';
import MdiBellOutline from '~icons/mdi/bell-outline';
import MdiAlertDecagram from '~icons/mdi/alert-decagram';
import { useAuthStore } from '@/stores/auth.js';
import { useStore } from '@/stores/store.js';
import { useShellModeStore } from '@/stores/shellMode.js';
import { logOutApi, getNotifications, markNotificationRead, markAllNotificationsRead, getUnreadNotificationCount, dismissNotification, clearReadNotifications } from '../../api/api.js';

const router    = useRouter();
const route     = useRoute();
const { t } = useI18n();
const authStore = useAuthStore();
const store     = useStore();
const shell     = useShellModeStore();

const tenantOpen  = ref(false);
const tenantError = ref(null);

// ── Notifications ──────────────────────────────────────────────────────────────
const notifOpen    = ref(false);
const notifLoading = ref(false);
const notifications = ref([]);
const unreadCountFromServer = ref(0);
const unreadCount = computed(() =>
  unreadCountFromServer.value > 0
    ? unreadCountFromServer.value
    : notifications.value.filter((n) => !n.is_read).length
);

let pollInterval = null;

async function refreshUnreadCount() {
  try {
    const res = await getUnreadNotificationCount();
    const newCount = res?.count ?? 0;
    if (newCount > unreadCountFromServer.value) {
      // New notifications arrived — refresh the list so it's ready when panel opens
      loadNotifications();
    }
    unreadCountFromServer.value = newCount;
  } catch {
    // Silently fail — fallback to local count
  }
}

async function loadNotifications() {
  notifLoading.value = true;
  try {
    const res = await getNotifications({ page_size: 30 });
    notifications.value = res.results ?? res ?? [];
  } catch { /* silent */ } finally {
    notifLoading.value = false;
  }
}

async function markAllRead() {
  try {
    await markAllNotificationsRead();
    notifications.value = notifications.value.map(n => ({ ...n, is_read: true }));
    unreadCountFromServer.value = 0;
  } catch { /* silent */ }
}

async function dismiss(n, event) {
  event.stopPropagation();
  try {
    await dismissNotification(n.id);
    notifications.value = notifications.value.filter(x => x.id !== n.id);
    if (!n.is_read && unreadCountFromServer.value > 0) unreadCountFromServer.value--;
  } catch { /* silent */ }
}

async function clearRead() {
  try {
    await clearReadNotifications();
    notifications.value = notifications.value.filter(n => !n.is_read);
  } catch { /* silent */ }
}

function notificationRoute(n) {
  const m = n.metadata ?? {}
  switch (n.notification_type) {
    case 'project_status_changed':
    case 'invoice_generated':
    case 'contract_ready_for_signing':
    case 'contract_signed':
      if (m.project_id) return `/project/${m.project_id}`
      break
    case 'checkout_created':
    case 'checkout_returned':
    case 'overdue_item':
    case 'upcoming_checkout':
    case 'checkout_reminder':
      if (m.project_id) return `/project/${m.project_id}`
      if (m.item_metadata_id) return `/instance/${m.item_metadata_id}`
      return '/checkouts'
    case 'item_condition_changed':
      if (m.item_metadata_id || m.item_id) return `/instance/${m.item_metadata_id ?? m.item_id}`
      break
    case 'transfer_dispatched':
    case 'transfer_received':
      if (m.transfer_id) return `/transfers/${m.transfer_id}`
      return '/transfers'
    case 'maintenance_completed':
      return '/maintenance/history'
    case 'maintenance_due':
      return '/maintenance/schedules'
    case 'low_stock':
    case 'restock_alert':
      return '/items'
    default:
      break
  }
  return null
}

function openNotification(n) {
  if (!n.is_read) {
    markNotificationRead(n.id).catch(() => {});
    const idx = notifications.value.findIndex(x => x.id === n.id);
    if (idx !== -1) notifications.value[idx] = { ...n, is_read: true };
  }
  notifOpen.value = false;
  const route = notificationRoute(n)
  if (route) router.push(route)
}

watch(notifOpen, (open) => {
  if (open) loadNotifications();
});

onMounted(() => {
  if (authStore.isAuthenticated) {
    loadNotifications();
    refreshUnreadCount();
  }
  pollInterval = setInterval(refreshUnreadCount, 60_000);
});

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval);
});

// Derive tenant list and current tenant name directly from the auth store
const tenants           = computed(() => authStore.availableTenants);
const currentTenantSlug = computed(() => authStore.currentTenant?.slug ?? null);
const currentTenantName = computed(() => authStore.currentTenant?.name ?? null);

const logout = async () => {
  await logOutApi();
};

const selectTenant = async (t) => {
  tenantError.value = null;
  tenantOpen.value  = false;
  if (t.slug === currentTenantSlug.value) return;
  try {
    await store.switchTenant(t.slug);
    window.location.reload();
  } catch (err) {
    // step_up_required is handled globally via App.vue event listener
    if (!err?.data?.step_up_required) {
      tenantError.value = err?.data?.error || t('tenantSwitcher.switchFailed');
    }
  }
};
</script>

<style scoped>
.dropdown-enter-active, .dropdown-leave-active { transition: opacity .12s ease, transform .12s ease; }
.dropdown-enter-from,  .dropdown-leave-to      { opacity: 0; transform: translateY(-4px); }
</style>
